import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { usePopupLogin } from '../../hooks/usePopupLogin';
import { CheckCircle, XCircle } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { inviteService } from '@/services/inviteService';

import { authService } from '@/services/authService';
import { toastUtils } from '@/common/utils/toastUtils';
import NotFound from '../NotFound';
import { useNavigationStore } from '@/common/store';
import { requestService } from '@/services/requestService';
import { CompanyRequest } from '@/common/types/request';
import { companyService, RegisterCompanyData, RegisterUserAndCompanyData } from '@/services/companyService';
import { extractDate } from '@/common/utils/dateFormat';

import Config from '@/config';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { commonService } from '@/services/commonService';
import { getBirthGender, removeSpaces } from '@/common/utils/commonUtil';
import { semanticColors } from '@/common/styles/colorTokens';
import { colorTokens } from '@/common/styles/colorTokens';
import { InvitationLayout } from '@/pages/invitation/layout/InvitationLayout';
import { StepItemType } from '@/common/components/step/Step';
import Avatar from '@/common/components/avatar/Avatar';
import { typography } from '@/common/styles/typographyTokens';
import { Label } from '@/components/ui/Label';
import ViewSwitcher from '@/common/components/viewSwitcher/ViewSwitcher';
import { LabelInput } from '@/common/components/input/LabelInput';
import { Input } from '@/common/components/input/Input';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { Button } from '@/common/components/button/Button';
import { AICompanyInfoModal } from './components/AICompanyInfoModal';
import { FileUploadModal } from './components/FileUploadModal';
import { AILoadingModal } from './components/AILoadingModal';
import { aiService } from '@/services/aiService';
import Section from '@/common/components/section/Section';
import { fileService } from '@/services/fileService';

type RequestUserType = 'MEMBER_AND_HASCOMPANY' | 'MEMBER_AND_NOCOMPANY' | 'NO_MEMBER_AND_NOCOMPANY'

// type PortfolioStep = 'INITIAL' | 'SIGNUP' | 'COMPANY' | 'PORTFOLIO' | 'WELCOME' | 'COMPLETE' | 'REJECTED'

enum PortfolioStep {
  INITIAL,
  SIGNUP,
  COMPANY,
  PORTFOLIO,
  WELCOME,
  COMPLETE,
  REJECTED
}

// WelcomeStep 컴포넌트를 외부로 분리
interface WelcomeStepProps {
  notificationId: string | null;
  companyCode: string;
  navigate: ReturnType<typeof useNavigate>;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ notificationId, companyCode, navigate }) => {
  const [countdown, setCountdown] = useState<number>(3);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigateToStartupSpace();
          return 0;
        }
        return prev - 1;
      });
    }, 1000); // 1000ms = 1초
    
    return () => clearInterval(timer);
  }, []); // 빈 배열이 아닌 초기에만 실행되도록 설정
  
  const navigateToStartupSpace = () => {
    window.location.replace(`${Config.startupClientUrl}/${companyCode}/request/regularly-report?n_id=${notificationId}`);
  };
  
  return (
    <Section
      title="환영합니다!"
      description={`회원가입이 완료되었습니다. ${countdown}초 후 스타트업 스페이스로 넘어갑니다.`}
    >
      <WelcomeContent>
        <SuccessAnimation>
          <CheckCircle size={64} color="#22C55E" />
        </SuccessAnimation>
        <Button onClick={navigateToStartupSpace} $size='sm'>
          바로 이동하기
        </Button>
      </WelcomeContent>
    </Section>
  );
};

const InvitationRegularlyReportPage: React.FC = () => {
  const navigate = useNavigate();
  const {  } = useNavigationStore();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();

  const [currentStep, setCurrentStep] = useState<PortfolioStep>(PortfolioStep.INITIAL);
  const [inviteInfo, setInviteInfo] = useState<CompanyRequest | null>(null);
  const [isPageNotFound, setIsPageNotFound] = useState(false);
  const [notificationId, setNotificationId] = useState<string | null>(null);
  const [requestUserType, setRequestUserType] = useState<RequestUserType | undefined>(undefined);
  const [differentUser, setDifferentUser] = useState<boolean>(false);
  const [rejectReason, setRejectReason] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // 회원가입 관련 상태
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState<boolean>(false);
  const [certifyData, setCertifyData] = useState<InisisAuthResult | null>(null);
  
  const [password, setPassword] = useState<string>('');
  const [passwordConfirm, setPasswordConfirm] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  
  // 회사 정보 관련 상태
  const [companyId, setCompanyId] = useState<number|undefined>(undefined);
  const [companyDataErrors, setCompanyDataErrors] = useState<Record<string, string>>({});
  const [isCompanyEmail, setIsCompanyEmail] = useState<boolean>(true);
  
  // AI 모달 관련 상태
  const [isAIModalOpen, setIsAIModalOpen] = useState<boolean>(false);
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState<boolean>(false);
  const [isAILoadingModalOpen, setIsAILoadingModalOpen] = useState<boolean>(false);
  
  // AI 분석으로 채워진 필드들을 추적하는 상태
  const [aiFilledFields, setAiFilledFields] = useState<Record<string, boolean>>({});
  const [isCertifiedCompany, setIsCertifiedCompany] = useState<boolean>(false);
  
  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyNameEn: '',
    ceoName: '',
    businessNumber: '',
    corporateNumber: '',
    establishedDate: '',
    ceoEmail: '',
    companyEmail: '',
    companyCode: ''
  });

  // 기존 상태 관리 부분에 추가
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    passwordConfirm: '',
    residentNumber: '',
    userDi: '',
    userCi: '',
    token: ''
  });
  
  // AI 필드에 사용할 색상
  const aiFieldColor = colorTokens.brand[800];

  // 로그인 팝업
  const { openPopup } = usePopupLogin({
    onSuccess: async (data) => {
      setIsLoading(true);
      try {
        const responseUser = await authService.setUserProfile();
        
        if(!responseUser.success || !responseUser.data) {
          toastUtils.error('사용자 정보를 가져오는 데 실패했습니다.');
          return;
        }
        if(!inviteInfo) {
          toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
          return;
        }

        // 이름과 이메일 확인
        const isNameMatch = responseUser.data.name === inviteInfo.receiver.name;
        const isEmailMatch = responseUser.data.email === inviteInfo.receiver.email || 
                            responseUser.data.companyUsers.some((companyUser: any) => companyUser.email === inviteInfo.receiver.email);

        if (!isNameMatch || !isEmailMatch) {
          console.log('responseUser.data', responseUser.data);
          console.log('inviteInfo.receiver', inviteInfo.receiver);
          toastUtils.error('초대된 사용자와 일치하지 않습니다.');
          setDifferentUser(true);
          return;
        }

        if(responseUser.data.name !== inviteInfo.receiver.name || (responseUser.data.email !== inviteInfo.receiver.email && !responseUser.data.companyUsers.some((companyUser: any) => companyUser.email === inviteInfo.receiver.email))) {
          console.log('responseUser.data', responseUser.data);
          console.log('inviteInfo.receiver', inviteInfo.receiver);
          toastUtils.error('초대된 사용자와 일치하지 않습니다.');
          setDifferentUser(true);
          return;
        }

        if(inviteInfo.receiver.ceoName && inviteInfo.receiver.businessNumber) {
          //로그인된 유저가 지원할 수 있는 회사 목록 조회
          const response = await authService.getRepresentativeCompany('portfolioInfo');
          if(!response) {
            toastUtils.error('회사 정보를 가져오는 데 실패했습니다.');
            return;
          }
          const matchedCompanyUser = response.companyUsers.find((companyUser: any) => companyUser.company.businessNumber === inviteInfo.receiver.businessNumber);
          if(matchedCompanyUser) {
            window.location.replace(`${Config.startupClientUrl}/${matchedCompanyUser.company.code}/request/regularly-report?n_id=${notificationId}`);
          } else {
            const response = await companyService.checkExistCompany(inviteInfo.receiver.businessNumber);
            if(response.isExist) {
              // Todo - 이미 있는 회사고 이 유저는 권한이 없다는 화면 셋팅
              toastUtils.error('이미 등록된 회사이지만, 귀하는 해당 회사의 권한이 없습니다.');
            } else {
              setRequestUserType('MEMBER_AND_NOCOMPANY');
              setCurrentStep(PortfolioStep.COMPANY);
            }
          }  
        } else {
          setRequestUserType('MEMBER_AND_NOCOMPANY');
          setCurrentStep(PortfolioStep.COMPANY);
        }
      } catch (error) {
        console.error('로그인 처리 중 오류 발생:', error);
        toastUtils.error('로그인 처리 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    }
  });

  // 초기 데이터 로드
  const getInviteInfo = async () => {
    setIsLoading(true);
    try {
      const nId = searchParams.get('n_id');
      console.log('nId', nId);
      if (!nId) {
        setIsPageNotFound(true);
        return;
      }

      setNotificationId(nId);
      const response = await requestService.getRequestCompany(nId);
       
      if (response) {
        setInviteInfo(response);      
        setCurrentStep(PortfolioStep.INITIAL);
      } else {
        setIsPageNotFound(true);
      }
    } catch (error) {
      console.error('초대 정보를 가져오는 중 오류 발생:', error);
      toastUtils.error('초대 정보를 가져오는 데 실패했습니다.');
      setIsPageNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 간편인증 처리
  const handleCertify = async (certifyData: InisisAuthResult) => {
    setIsLoading(true);
    try {
      console.log('certifyData', certifyData);
      console.log('inviteInfo', inviteInfo);
      if (removeSpaces(certifyData.userName) !== removeSpaces(inviteInfo?.receiver?.name || '')) {
        toastUtils.error('초대된 사용자와 일치하지 않습니다.');
        setDifferentUser(true);
        return;
      }

      setIsCertifiedPersonal(true);
      setCertifyData(certifyData);
      setFormData(prev => ({
        ...prev,
        name: certifyData.userName,
        phone: certifyData.userPhone,
        residentNumber: getBirthGender(certifyData.userBirth, certifyData.userGender)+'******',
        userDi: certifyData.userDi,
        userCi: certifyData.userCi,
        token: certifyData.token
      }));

      if(certifyData.isDuplicate) {
        setRequestUserType('MEMBER_AND_NOCOMPANY');
        setCurrentStep(PortfolioStep.PORTFOLIO);
      } else {
        setRequestUserType('NO_MEMBER_AND_NOCOMPANY');
        setCurrentStep(PortfolioStep.SIGNUP);
      }
    } catch (error) {
      console.error('인증 처리 중 오류 발생:', error);
      toastUtils.error('인증 처리 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 유효성 검사
  const validatePassword = (password: string): boolean => {
    // 최소 4자 이상
    const isValid = password.length >= 4;
    
    if (!isValid) {
      setPasswordError('비밀번호는 4자 이상이어야 합니다.');
    } else {
      setPasswordError('');
    }
    
    return isValid;
  };


  //중복 정보 유효성 검사
  const validateDuplicateInfo = async (type: string, value: string) => {
    const response = await commonService.checkDuplicateValue(type, value);

    if(type === 'companyCode') {
      response.exists && setCompanyDataErrors(prev => ({ ...prev, companyCode: '이미 등록된 회사 코드입니다.' }));
    } else if(type === 'email') {
      response.exists && setCompanyDataErrors(prev => ({ ...prev, companyEmail: '이미 등록된 이메일입니다.' }));
    } else if(type === 'businessNumber') {
      response.exists && setCompanyDataErrors(prev => ({ ...prev, businessNumber: '이미 등록된 사업자번호입니다.' }));
    }
  };

  // AI 모달 핸들러 업데이트
  const handleAIModalOpen = () => {
    // setIsAIModalOpen(true);
  };

  const handleAIModalClose = () => {
    setIsAIModalOpen(false);
  };

  const handleAIConfirm = () => {
    // AI 기능 사용 - 파일 업로드 모달 열기
    setIsAIModalOpen(false);
    setIsFileUploadModalOpen(true);
  };

  const handleManualConfirm = () => {
    // 수동 입력 로직 구현
    setIsAIModalOpen(false);
    // 현재 페이지에서 계속 진행
    toastUtils.info('수동으로 기업 정보를 입력합니다.');
  };

  // 파일 업로드 모달 핸들러
  const handleFileUploadModalClose = () => {
    setIsFileUploadModalOpen(false);
  };

  const handleFileUpload = async (file: File) => {
    setIsFileUploadModalOpen(false);
    setIsAILoadingModalOpen(true);

    try {
      const fileResult = await fileService.uploadFile([file]);

      if(!fileResult) return toastUtils.error('파일 업로드에 실패했습니다. 파일을 확인해주세요.');

      const response = await aiService.getAnalyzeBusinessRegistration({
        fileId: fileResult[0].id
      });
      
      if (response) {
        // 사업자번호 비교 (하이픈 제거 후 비교)
        const inviteBusinessNumber = inviteInfo?.receiver?.businessNumber?.replace(/-/g, '') || '';
        const aiBusinessNumber = response.businessNumber.replace(/-/g, '');
        
        if (inviteBusinessNumber !== aiBusinessNumber) {
          toastUtils.error('사업자 번호가 다릅니다.');
          setIsAILoadingModalOpen(false);
          return;
        }

        // 사업자번호가 일치하면 필드 업데이트
        const updatedCompanyData = {
          ...companyData,
          companyName: response.companyName,
          ceoName: response.representative,
          businessNumber: formatBusinessNumber(response.businessNumber),
          corporateNumber: formatCorporateNumber(response.corporateNumber),
          establishedDate: formatFoundingDate(response.establishmentDate)
        };

        setCompanyData(updatedCompanyData);
        
        // AI로 채워진 필드들 표시
        setAiFilledFields({
          companyName: true,
          ceoName: true,
          businessNumber: true,
          corporateNumber: true,
          establishedDate: true
        });

        setIsAILoadingModalOpen(false);
        toastUtils.success('AI 분석이 완료되어 기업 정보가 자동으로 입력되었습니다.');
        
      } else {
        throw new Error('AI 분석 실패');
      }
    } catch (error) {
      console.error('AI 분석 중 오류 발생:', error);
      toastUtils.error('AI 분석 중 오류가 발생했습니다. 수동으로 입력해주세요.');
      setIsAILoadingModalOpen(false);
    }
  };

  // AI 로딩 모달 핸들러
  const handleAILoadingModalClose = () => {
    setIsAILoadingModalOpen(false);
  };

  // AI 채움 상태 리셋 함수
  const handleFieldChange = (fieldName: string, value: string) => {
    setCompanyData(prev => ({ ...prev, [fieldName]: value }));
    // 사용자가 값을 변경하면 해당 필드의 AI 채움 상태를 false로 변경
    if (aiFilledFields[fieldName]) {
      setAiFilledFields(prev => ({ ...prev, [fieldName]: false }));
      setIsCertifiedCompany(false);
    }
  };

  // 포맷팅 헬퍼 함수들
  const formatBusinessNumber = (businessNumber: string) => {
    const cleanNumber = businessNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 10) {
      return `${cleanNumber.slice(0,3)}-${cleanNumber.slice(3,5)}-${cleanNumber.slice(5)}`;
    }
    return businessNumber;
  };

  const formatCorporateNumber = (corporateNumber: string) => {
    const cleanNumber = corporateNumber.replace(/[^0-9]/g, '');
    if (cleanNumber.length === 13) {
      return `${cleanNumber.slice(0, 6)}-${cleanNumber.slice(6)}`;
    }
    return corporateNumber;
  };

  const formatFoundingDate = (foundingDate: string) => {
    // YYYYMMDD 형식을 YYYY-MM-DD로 변환
    const cleanDate = foundingDate.replace(/[^0-9]/g, '');
    if (cleanDate.length === 8) {
      return `${cleanDate.slice(0,4)}-${cleanDate.slice(4,6)}-${cleanDate.slice(6)}`;
    }
    return foundingDate;
  };

  const checkCompanyStatus = async () => {
    const response = await companyService.checkCompanyStatus({
      companyName: companyData.companyName,
      ceoName: companyData.ceoName,
      businessNumber: companyData.businessNumber.replace(/-/g, ''),
      corporateNumber: companyData.corporateNumber.replace(/-/g, ''),
      establishedDate: extractDate(companyData.establishedDate).replace(/-/g, '')
    });
    if (response.isExistCompany) {
      if (!isCertifiedCompany) {
        toastUtils.success('기업 정보 인증이 완료되었습니다.');
      }
      setIsCertifiedCompany(true);
    } else {
      toastUtils.error('기업 정보 인증이 불가합니다. 입력한 정보를 확인하시거나 잠시 후 다시 시도하세요.');
    }
    return response;
  };

  // 회사 정보 입력 완료
  const handleCompanyComplete = async () => {

    setIsLoading(true);
    try {
      const response = await checkCompanyStatus();


      if (response.isExistCompany) {
        //회원 가입
        if(requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
          const bodyData:RegisterUserAndCompanyData = {
            notificationId: notificationId!,
            companyEmail: companyData.companyEmail,
            companyEmailVerified: true,
            companyCode: companyData.companyCode,
            isVerified: true,
            isPreStartup: false,
            isCeo: formData.name === companyData.ceoName,
            companyName: companyData.companyName,
            companyNameEn: companyData.companyNameEn || undefined,
            businessNumber: companyData.businessNumber,
            isBusinessVerified: true,
            foundingDate: formatFoundingDate(companyData.establishedDate),
            residentNumber: formData.residentNumber,
            ceoName: companyData.ceoName,
            ceoEmail: companyData.ceoEmail,
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            emailVerified: true,
            password: formData.password,
            userDi: formData.userDi,
            userCi: formData.userCi,
            token: formData.token
          }
          const response = await companyService.registerUserAndCompany(bodyData);
          if(response.success) {
            setCurrentStep(PortfolioStep.WELCOME);
          } else {
            toastUtils.error('회원 가입 중 오류가 발생했습니다.');
          }
        }else if(requestUserType === 'MEMBER_AND_NOCOMPANY') {
          //회사 등록
          const bodyData:RegisterCompanyData = {
            notificationId: notificationId!,
            companyEmail: companyData.companyEmail,
            companyEmailVerified: true,
            companyCode: companyData.companyCode,
            isVerified: true,
            isPreStartup: false,
            isCeo: user?.name === companyData.ceoName,
            companyName: companyData.companyName,
            companyNameEn: companyData.companyNameEn || undefined,
            businessNumber: companyData.businessNumber,
            isBusinessVerified: true,
            foundingDate: formatFoundingDate(companyData.establishedDate),
            ceoName: companyData.ceoName,
            ceoEmail: companyData.ceoEmail
          }
          const response = await companyService.registerCompany(bodyData);
          if(response.success) {
            setCurrentStep(PortfolioStep.WELCOME);
          } else {
            toastUtils.error('회사 등록 중 오류가 발생했습니다.');
          }
        }
        
      } else {
        toastUtils.info('확인할 수 없는 회사입니다.');
      }
    } catch (error) {
      console.error('회사 정보 확인 중 오류 발생:', error);
      toastUtils.error('회사 정보 확인 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 초대 거절
  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toastUtils.error('거절 사유를 입력해주세요.');
      return;
    }

    setIsLoading(true);
    try {
      const response = await inviteService.rejectInvite({
        notificationId: notificationId!,
        reason: rejectReason
      });

      if (response?.success) {
        getInviteInfo();
        setCurrentStep(PortfolioStep.REJECTED);
        toastUtils.success('초대가 성공적으로 거절되었습니다.');
      } else {
        throw new Error('거절 처리 실패');
      }
    } catch (error) {
      console.error('초대 거절 중 오류 발생:', error);
      toastUtils.error('초대 거절 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // firstInit 함수 복원
  const firstInit = async (inviteInfo: CompanyRequest) => {
    try {

      setFormData(prev => ({...prev, name: inviteInfo.receiver.name}));
      setCompanyData(prev => ({...prev, ceoName: inviteInfo.receiver.ceoName || "", businessNumber: inviteInfo.receiver.businessNumber || ""}));

      
      if(user) {
        console.log('user 있음');
        console.log('user', user);
        console.log('inviteInfo', inviteInfo);
        if(user.name === inviteInfo.receiver.name && (user.email === inviteInfo.receiver.email || user.companyUsers.some((companyUser: any) => companyUser.email === inviteInfo.receiver.email))) {
          console.log('user 이름과 이메일 일치');
          if(inviteInfo.receiver.ceoName && inviteInfo.receiver.businessNumber) {
            //로그인된 유저가 지원할 수 있는 회사 목록 조회
            const response = await authService.getRepresentativeCompany('portfolioInfo');
            if(!response) {
              toastUtils.error('회사 정보를 가져오는 데 실패했습니다.');
              return;
            }
            const matchedCompanyUser = response.companyUsers.find((companyUser: any) => companyUser.company.businessNumber === inviteInfo.receiver.businessNumber);

            console.log('matchedCompanyUser', matchedCompanyUser);
            if(matchedCompanyUser) {
              window.location.replace(`${Config.startupClientUrl}/${matchedCompanyUser.company.code}/request/regulary-report?n_id=${notificationId}`);
            } else {
              const response = await companyService.checkExistCompany(inviteInfo.receiver.businessNumber);
              if(response.isExist) {
                // Todo - 이미 있는 회사고 이 유저는 권한이 없다는 화면 셋팅
                toastUtils.error('이미 등록된 회사이지만, 귀하는 해당 회사의 권한이 없습니다.');
              } else {
                setRequestUserType('MEMBER_AND_NOCOMPANY');
                setCurrentStep(PortfolioStep.COMPANY);
              }
            }  
          } else {
            setRequestUserType('MEMBER_AND_NOCOMPANY');
            setCurrentStep(PortfolioStep.COMPANY);
          }
        }
      } else {      
        setCurrentStep(PortfolioStep.INITIAL);
      }  
    } catch (error) {
      console.error('초기화 중 오류 발생:', error);
      toastUtils.error('페이지 초기화 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    if (inviteInfo) {
      firstInit(inviteInfo);
    }
  }, [inviteInfo, user]);

  useEffect(() => {
    
    getInviteInfo();
  }, []);

  useEffect(() => {
    if (currentStep === PortfolioStep.COMPANY) {
      handleAIModalOpen();
    }
  }, [currentStep]);

  // AI로 채워진 필드들이 모두 true일 때 자동으로 회사 상태 확인
  useEffect(() => {
    const allAIFieldsFilled = aiFilledFields.companyName && 
                              aiFilledFields.ceoName && 
                              aiFilledFields.businessNumber && 
                              aiFilledFields.corporateNumber && 
                              aiFilledFields.establishedDate;
    
    if (allAIFieldsFilled && companyData.companyName && companyData.ceoName && 
        companyData.businessNumber && companyData.corporateNumber && companyData.establishedDate) {
      const autoCheckCompanyStatus = async () => {
        try {
          const statusResponse = await companyService.checkCompanyStatus({
            companyName: companyData.companyName,
            ceoName: companyData.ceoName,
            businessNumber: companyData.businessNumber.replace(/-/g, ''),
            corporateNumber: companyData.corporateNumber.replace(/-/g, ''),
            establishedDate: extractDate(companyData.establishedDate).replace(/-/g, '')
          });
          if(statusResponse.isExistCompany) {
            setIsCertifiedCompany(true);
          }
        } catch (error) {
          console.error('자동 회사 상태 확인 중 오류:', error);
        }
      };
      
      autoCheckCompanyStatus();
    }
  }, [aiFilledFields, companyData.companyName, companyData.ceoName, companyData.businessNumber, companyData.corporateNumber, companyData.establishedDate]);

  // isCompanyEmail 상태 변경 시 이메일 배치
  useEffect(() => {
    if (inviteInfo?.receiver.email) {
      if (isCompanyEmail) {
        // 회사용 계정인 경우: 초대 이메일을 회사 이메일로 설정
        setCompanyData(prev => ({ ...prev, companyEmail: inviteInfo.receiver.email }));
        setFormData(prev => ({ ...prev, email: '' })); // 개인 이메일은 초기화
      } else {
        // 개인 계정인 경우: 초대 이메일을 개인 이메일로 설정
        setFormData(prev => ({ ...prev, email: inviteInfo.receiver.email }));
        setCompanyData(prev => ({ ...prev, companyEmail: '' })); // 회사 이메일은 초기화
      }
    }
  }, [isCompanyEmail, inviteInfo?.receiver.email]);

  if (isPageNotFound) return <NotFound />;
  if (!inviteInfo) return <LoadingContainer><div>초대 정보를 로드 중입니다...</div></LoadingContainer>;

  // 기존 steps 정의 부분을 수정
  const getSteps = () => {
    // 초기 화면에서는 첫 단계만 표시
    if (requestUserType === undefined) {
      return [
        { id: PortfolioStep.INITIAL, label: '초대 확인' }
      ];
    }

    // 회원가입 후 진행 시
    if (requestUserType === 'NO_MEMBER_AND_NOCOMPANY') {
      return [
        { id: PortfolioStep.INITIAL, label: '초대 확인' },
        { id: PortfolioStep.SIGNUP, label: '회원 가입' },
        { id: PortfolioStep.COMPANY, label: '회사 등록' },
        { id: PortfolioStep.WELCOME, label: '가입 완료' }
      ];
    }

    // 로그인 후 회사 없음
    if (requestUserType === 'MEMBER_AND_NOCOMPANY') {
      return [
        { id: PortfolioStep.INITIAL, label: '초대 확인' },
        { id: PortfolioStep.COMPANY, label: '회사 등록' },
        { id: PortfolioStep.WELCOME, label: '가입 완료' }
      ];
    }

    // 로그인 후 회사 있음
    if (requestUserType === 'MEMBER_AND_HASCOMPANY') {
      return [
        { id: PortfolioStep.INITIAL, label: '초대 확인' },
        { id: PortfolioStep.WELCOME, label: '가입 완료' }
      ];
    }

    // 기본값 (초대 확인 단계)
    return [
      { id: PortfolioStep.INITIAL, label: '초대 확인' }
    ];
  };

  // Step 1: 초기 화면
  const InitialStep = () => (
    <Section
      title={inviteInfo.receiver.investee ? `${inviteInfo.receiver.investee} 정기보고 제출 요청` : '정기 보고서 정보 요청'}
    >
      {differentUser ? (
        <>
          <AlertBox $variant="error">
            <XCircle size={20} />
            <AlertText>
              초대된 사용자와 다른 사용자입니다. 자동적으로 거절 처리됩니다.
              <br />
              초대 정보를 확인하시고 틀린 정보가 있다면 자세한 사유를 작성해주세요.
              <small>
                * 사유는 초대자에게 전달됩니다.
              </small>
            </AlertText>
          </AlertBox>
          <FormItem>  
            <textarea
              style={{
                width: '100%', 
                height: 120, 
                padding: 16, 
                border: '1px solid #e2e8f0', 
                borderRadius: 8,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
                transition: 'border-color 0.2s',
                fontSize: '14px'
              }}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="거절 사유를 입력해주세요"
            />
          </FormItem>

          <ButtonGroup>
            <Button onClick={() => setDifferentUser(false)} $variant='outline' $size='sm'>
              다시하기
            </Button>
            <Button 
              disabled={!rejectReason.trim() || isLoading} 
              onClick={handleReject}
              $size='sm'
            >
              {isLoading ? '처리 중...' : '거절하기'}
            </Button>
          </ButtonGroup>
        </>
      ) : (
        <RequestMessageContainer>
          <FirstMessageRow>
            <Avatar alt={inviteInfo.sender.companyName} isUser={false}/>
            <RequestMessage>{inviteInfo.sender.name}님께서 {inviteInfo.receiver.name}님에게 {inviteInfo.receiver.investee ? `${inviteInfo.receiver.investee}의` : ''} {inviteInfo.sender.year ? `${inviteInfo.sender.year}년 ${inviteInfo.sender.quarter}분기` : ''} 정기보고 제출을 요청했습니다.</RequestMessage>
          </FirstMessageRow>
          <RequestMessage>계속 진행하시려면 간편 인증을 진행하시거나 로그인을 해주세요.</RequestMessage>
          <ButtonGroup>
            <InisisAuthButton 
              $variant='fill'
              onSuccess={(data) => {
                handleCertify(data);
              }}
              $title='간편 인증'
              $size='sm'
            />
            <Button 
              $variant='outline'
              onClick={openPopup}
              disabled={isLoading}
              $size='sm'
            >
              {isLoading ? '처리 중...' : '로그인'}
            </Button>
          </ButtonGroup>
        </RequestMessageContainer>
      )}
    </Section>
  );

  // SignupStep 컴포넌트
  const SignupStep = () => (
    <Section
      title="회원 정보 입력"
      description="회원가입을 위한 정보를 입력해주세요."
    >
      <SignupFormContainer>
        {/* 이름, 휴대폰 번호 행 */}
        <FormRow>
          <FormField>
            <LabelInput label='이름' required={true} value={formData.name} disabled={true} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} />
          </FormField>
          <FormField>
            <LabelInput label='휴대폰 번호' required={true} value={formData.phone} disabled={true} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} />
          </FormField>
        </FormRow>

        {/* 이메일 주소 행 */}
        <FormRow>
          <FormField style={{ width: '100%' }}>
            <LabelInput 
              label='이메일 주소' 
              required={true} 
              value={isCompanyEmail ? companyData.companyEmail : formData.email} 
              disabled={true} 
              onChange={() => {}} 
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField style={{ width: '100%' }}>
            <FieldLabel>위 이메일 주소가 회사용 계정인가요? <RequiredMark>*</RequiredMark></FieldLabel>
            <ViewSwitcher options={[{label: '네, 회사용 계정입니다.'}, {label: '아니오, 개인 계정입니다.'}]} selectedIndex={isCompanyEmail ? 0 : 1} onChange={(index) => setIsCompanyEmail(index === 0)} />
          </FormField>
        </FormRow>

        {/* 추가 이메일 주소 행 */}
        <FormRow>
          <FormField style={{ width: '100%' }}>
            <FormRow>
              <LabelInput
                label={`${isCompanyEmail ? '개인 이메일 주소' : '회사 이메일 주소'}`}
                required={false}
                type="text"
                value={isCompanyEmail ? formData.email : companyData.companyEmail}
                onChange={(e) => {
                  if (isCompanyEmail) {
                    setFormData(prev => ({ ...prev, email: e.target.value }));
                  } else {
                    setCompanyData(prev => ({ ...prev, companyEmail: e.target.value }));
                  }
                }}
                placeholder={`${isCompanyEmail ? '개인' : '회사'} 이메일을 입력해주세요`}
              />
            </FormRow>
            <InfoText>{isCompanyEmail ? '개인 이메일 계정을 함께 등록하시면, 팩트시트를 더욱 편리하게 사용하실 수 있어요.' : '회사용 이메일 계정을 등록하시면 개인 이메일 주소를 노출하지 않고 사용하실 수 있습니다.'}</InfoText>
          </FormField>
        </FormRow>

        {/* 비밀번호, 비밀번호 확인 행 */}
        <PasswordFormRow>
          <FormField style={{ flex: 1 }}>
            <LabelInput
              label="비밀번호"
              type="password"

              value={password}
              onChange={(e) => {
                const cleanValue = removeSpaces(e.target.value);
                setPassword(cleanValue);
                validatePassword(cleanValue);
                if (passwordConfirm !== cleanValue) {
                  setPasswordError('비밀번호가 일치하지 않습니다.');
                } else {
                  setPasswordError('');
                }
              }}
              placeholder="비밀번호를 입력해주세요"
            />
          </FormField>
          <FormField style={{ flex: 1 }}>
            <LabelInput
              label="비밀번호 확인"
              type="password"

              value={passwordConfirm}
              onChange={(e) => {
                const cleanValue = removeSpaces(e.target.value);
                setPasswordConfirm(cleanValue);
                if (password !== cleanValue) {
                  setPasswordError('비밀번호가 일치하지 않습니다.');
                } else {
                  setPasswordError('');
                }
              }}
              placeholder="비밀번호를 다시 입력해주세요"
              error={!!passwordError}
              errorMessage='비밀번호가 일치하지 않습니다.'
            />
          </FormField>
        </PasswordFormRow>

      </SignupFormContainer>

      <ButtonContainer>
        <Button 
          $variant='outline'
          onClick={() => setCurrentStep(PortfolioStep.INITIAL)}
          $size='sm'
        >
          이전
        </Button>
        <Button 
          onClick={() => {
            setFormData(prev => ({...prev, password: password, passwordConfirm: passwordConfirm}));
            setCurrentStep(PortfolioStep.COMPANY);
          }}
          disabled={isLoading || !!passwordError || !password || !passwordConfirm || !formData.email || !companyData.companyEmail}
          $size='sm'
        >
          다음
        </Button>
      </ButtonContainer>
    </Section>
  );

  // CompanyStep 컴포넌트
  const CompanyStep = () => (
    <Section
      title="기업 정보 확인"
      description="기업 정보를 입력하고 인증을 완료해주세요."
    >

      
      <CompanyFormContainer>
        {inviteInfo?.receiver.ceoName && inviteInfo?.receiver.businessNumber ? (
          <>
            {/* 기업명, 영문 기업명 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='기업명' 
                  value={companyData.companyName}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('companyName', value)}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    if(e.target.value.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, companyName: '기업명을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { companyName, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="기업명을 입력해주세요"
                  error={!!companyDataErrors.companyName}
                  errorMessage="기업명을 입력해주세요."
                  color={aiFilledFields.companyName ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <LabelInput 
                  label='영문 기업명' 
                  value={companyData.companyNameEn}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => setCompanyData(prev => ({ ...prev, companyNameEn: value }))}
                  placeholder="영문 기업명을 입력해주세요"
                />
              </FormField>
            </FormRow>

            {/* 대표자명, 사업자번호 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='대표자명' 
                  value={companyData.ceoName}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('ceoName', value)}
                  onBlur={() => {
                    if(companyData.ceoName.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, ceoName: '대표자명을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { ceoName, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="대표자명을 입력해주세요"
                  error={!!companyDataErrors.ceoName}
                  errorMessage="대표자명을 입력해주세요."
                  color={aiFilledFields.ceoName ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <FieldLabel $isAIFilled={aiFilledFields.businessNumber}>
                  <LabelText>사업자번호</LabelText>
                </FieldLabel>
                <ReadOnlyInput $isAIFilled={aiFilledFields.businessNumber}>
                  <ReadOnlyText $isAIFilled={aiFilledFields.businessNumber}>{companyData.businessNumber}</ReadOnlyText>
                </ReadOnlyInput>
              </FormField>
            </FormRow>

            {/* 법인번호, 설립일 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='법인번호' 
                  value={companyData.corporateNumber}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => {
                    // 하이픈 자동 추가 (ex: 1234567890123 -> 123456-7890123)
                    const cleanValue = value.replace(/[^0-9]/g, '');
                    if (cleanValue.length <= 13) {
                      const formatted = cleanValue.length > 6 
                        ? `${cleanValue.slice(0, 6)}-${cleanValue.slice(6)}` 
                        : cleanValue;
                      handleFieldChange('corporateNumber', formatted);
                    }
                  }}
                  onBlur={() => {
                    if(companyData.corporateNumber.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, corporateNumber: '법인번호를 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { corporateNumber, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="법인번호를 입력해주세요"
                  error={!!companyDataErrors.corporateNumber}
                  errorMessage="법인번호를 입력해주세요."
                  color={aiFilledFields.corporateNumber ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <LabelInput 
                  label='설립일' 
                  type="date"
                  value={companyData.establishedDate}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('establishedDate', value)}
                  onBlur={() => {
                    if(companyData.establishedDate.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, establishedDate: '설립일을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { establishedDate, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="설립일을 입력해주세요"
                  error={!!companyDataErrors.establishedDate}
                  errorMessage="설립일을 입력해주세요."
                  color={aiFilledFields.establishedDate ? aiFieldColor : undefined}
                />
              </FormField>
            </FormRow>

            <FormRow>
              <FormField style={{ alignItems: 'flex-end' }}>
                <Button $size='sm' onClick={checkCompanyStatus} $variant={isCertifiedCompany ? 'fill' : 'outline'} $isDisabled={isCertifiedCompany}>{isCertifiedCompany ? '기업 정보 인증 완료' : '기업 정보 인증하기'}</Button>
              </FormField>
            </FormRow>

            {/* 대표자 이메일 행 */}
            {formData.name !== companyData.ceoName && (
              <FormRow>
                <FormField style={{ width: '100%' }}>
                  <LabelInput
                    label='대표자 이메일'
                    value={companyData.ceoEmail}
                    onChangeData={(value: string) => setCompanyData(prev => ({ ...prev, ceoEmail: value }))}
                    placeholder="대표자 이메일을 입력해주세요"
                    onBlur={() => {
                      if(companyData.ceoEmail.length === 0) {
                        setCompanyDataErrors(prev => ({ ...prev, ceoEmail: '대표자 이메일을 입력해주세요.' }));
                      }else{
                        setCompanyDataErrors(prev => {
                          const { ceoEmail, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    error={!!companyDataErrors.ceoEmail}
                    errorMessage="대표자 이메일을 입력해주세요."
                  />
                </FormField>
              </FormRow>
            )}

            {/* 회사 코드 행 */}
            <FormRow>
              <FormField>
                <LabelInput
                  label='회사 코드'
                  value={companyData.companyCode}
                  onChangeData={(value: string) => {
                    if(value.includes(" ")) {
                      setCompanyDataErrors(prev => ({ ...prev, companyCode: '회사 코드는 공백을 포함할 수 없습니다.' }));
                    } else {
                      setCompanyDataErrors(prev => ({ ...prev, companyCode: '' }));
                    }
                    setCompanyData(prev => ({ ...prev, companyCode: value }))
                  }}
                  onBlur={() => {
                    if(companyData.companyCode.length === 0) {
                      return setCompanyDataErrors(prev => ({ ...prev, companyCode: '회사 코드를 입력해주세요.' }));
                    }
                    setCompanyDataErrors(prev => {
                      const { companyCode, ...rest } = prev;
                      return rest;
                    });
                    validateDuplicateInfo('companyCode', companyData.companyCode)
                  }}
                  placeholder="회사 코드를 입력해주세요"
                  error={!!companyDataErrors.companyCode}
                  errorMessage="회사 코드를 입력해주세요."
                />
                <InfoText>회사에서 사용할 고유한 코드를 입력해주세요.</InfoText>
              </FormField>
            </FormRow>
          </>
        ) : (
          <>
            {/* 기업명, 사업자번호 행 */}
            <FormRow>
              <FormField>
                <LabelInput
                  label='기업명'
                  value={companyData.companyName}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('companyName', value)}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    if(e.target.value.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, companyName: '기업명을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { companyName, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="기업명을 입력해주세요"
                  error={!!companyDataErrors.companyName}
                  errorMessage="기업명을 입력해주세요."
                  color={aiFilledFields.companyName ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <LabelInput 
                  label='사업자번호' 
                  value={companyData.businessNumber}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => {
                    const cleanValue = value.replace(/[^0-9]/g, '');
                    if (cleanValue.length <= 10) {
                      const formatted = cleanValue.length > 5 
                        ? cleanValue.length > 7
                          ? `${cleanValue.slice(0,3)}-${cleanValue.slice(3,5)}-${cleanValue.slice(5)}`
                          : `${cleanValue.slice(0,3)}-${cleanValue.slice(3)}`
                        : cleanValue;
                      handleFieldChange('businessNumber', formatted);
                    }
                  }}
                  onBlur={() => {
                    if(companyData.businessNumber.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, businessNumber: '사업자번호를 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { businessNumber, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="사업자번호를 입력해주세요"
                  error={!!companyDataErrors.businessNumber}
                  errorMessage="사업자번호를 입력해주세요."
                  color={aiFilledFields.businessNumber ? aiFieldColor : undefined}
                />
              </FormField>
            </FormRow>

            {/* 법인번호, 설립일 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='법인번호' 
                  value={companyData.corporateNumber}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => {
                    // 하이픈 자동 추가 (ex: 1234567890123 -> 123456-7890123)
                    const cleanValue = value.replace(/[^0-9]/g, '');
                    if (cleanValue.length <= 13) {
                      const formatted = cleanValue.length > 6 
                        ? `${cleanValue.slice(0, 6)}-${cleanValue.slice(6)}` 
                        : cleanValue;
                      handleFieldChange('corporateNumber', formatted);
                    }
                  }}
                  onBlur={() => {
                    if(companyData.corporateNumber.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, corporateNumber: '법인번호를 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { corporateNumber, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="법인번호를 입력해주세요"
                  error={!!companyDataErrors.corporateNumber}
                  errorMessage="법인번호를 입력해주세요."
                  color={aiFilledFields.corporateNumber ? aiFieldColor : undefined}
                />
              </FormField>
              <FormField>
                <LabelInput 
                  label='설립일' 
                  type="date"
                  value={companyData.establishedDate}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('establishedDate', value)}
                  onBlur={() => {
                    if(companyData.establishedDate.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, establishedDate: '설립일을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { establishedDate, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="설립일을 입력해주세요"
                  error={!!companyDataErrors.establishedDate}
                  errorMessage="설립일을 입력해주세요."
                  color={aiFilledFields.establishedDate ? aiFieldColor : undefined}
                />
              </FormField>
            </FormRow>

            {/* 대표자명 행 */}
            <FormRow>
              <FormField>
                <LabelInput 
                  label='대표자명' 
                  value={companyData.ceoName}
                  disabled={isCertifiedCompany}
                  onChangeData={(value: string) => handleFieldChange('ceoName', value)}
                  onBlur={() => {
                    if(companyData.ceoName.length === 0) {
                      setCompanyDataErrors(prev => ({ ...prev, ceoName: '대표자명을 입력해주세요.' }));
                    }else{
                      setCompanyDataErrors(prev => {
                        const { ceoName, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  placeholder="대표자명을 입력해주세요"
                  error={!!companyDataErrors.ceoName}
                  errorMessage="대표자명을 입력해주세요."
                  color={aiFilledFields.ceoName ? aiFieldColor : undefined}
                />
              </FormField>
            </FormRow>

            <FormRow>
              <FormField style={{ alignItems: 'flex-end' }}>
                <Button $size='sm' onClick={checkCompanyStatus} $variant={isCertifiedCompany ? 'fill' : 'outline'} $isDisabled={isCertifiedCompany}>{isCertifiedCompany ? '기업 정보 인증 완료' : '기업 정보 인증하기'}</Button>
              </FormField>
            </FormRow>

            {/* 대표자 이메일 행 */}
            {formData.name !== companyData.ceoName && (
              <FormRow>
                <FormField style={{ width: '100%' }}>
                  <LabelInput
                    label='대표자 이메일'
                    value={companyData.ceoEmail}
                    onChangeData={(value: string) => setCompanyData(prev => ({ ...prev, ceoEmail: value }))}
                    placeholder="대표자 이메일을 입력해주세요"
                    onBlur={() => {
                      if(companyData.ceoEmail.length === 0) {
                        setCompanyDataErrors(prev => ({ ...prev, ceoEmail: '대표자 이메일을 입력해주세요.' }));
                      }else{
                        setCompanyDataErrors(prev => {
                          const { ceoEmail, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                    error={!!companyDataErrors.ceoEmail}
                    errorMessage="대표자 이메일을 입력해주세요."
                  />
                </FormField>
              </FormRow>
            )}

            {/* 회사 코드 행 */}
            <FormRow>
              <FormField>
                <LabelInput
                  label='회사 코드'
                  value={companyData.companyCode}
                  onChangeData={(value: string) => {
                    if(value.includes(" ")) {
                      setCompanyDataErrors(prev => ({ ...prev, companyCode: '회사 코드는 공백을 포함할 수 없습니다.' }));
                    } else {
                      setCompanyDataErrors(prev => ({ ...prev, companyCode: '' }));
                    }
                    setCompanyData(prev => ({ ...prev, companyCode: value }))
                  }}
                  onBlur={() => {
                    if(companyData.companyCode.length === 0) {
                      return setCompanyDataErrors(prev => ({ ...prev, companyCode: '회사 코드를 입력해주세요.' }));
                    }
                    setCompanyDataErrors(prev => {
                      const { companyCode, ...rest } = prev;
                      return rest;
                    });
                    validateDuplicateInfo('companyCode', companyData.companyCode)
                  }}
                  placeholder="회사 코드를 입력해주세요"
                  error={!!companyDataErrors.companyCode}
                  errorMessage="회사 코드를 입력해주세요."
                />
                <InfoText>회사에서 사용할 고유한 코드를 입력해주세요.</InfoText>
              </FormField>
            </FormRow>
          </>
        )}
      </CompanyFormContainer>

      <ButtonContainer>
        {/* <Button 
          onClick={() => {
            console.log('보낸사람에게 메세지 보내기');
          }} 
          $variant='outline' 
          $size='sm'
        >
          문의하기
        </Button> */}
        <Button 
          onClick={handleCompanyComplete}
          disabled={
            Object.keys(companyDataErrors).length > 0 || 
            !companyData.companyName || 
            !companyData.ceoName ||
            !companyData.businessNumber ||
            !companyData.corporateNumber ||
            !companyData.establishedDate ||
            !companyData.companyCode ||
            (formData.name !== inviteInfo?.receiver?.ceoName && !companyData.ceoEmail) ||
            !isCertifiedCompany
          }
          $size='sm'
        >
          {isLoading ? '처리 중...' : '가입'}
        </Button>
      </ButtonContainer>
    </Section>
  );

  const steps: StepItemType[] = getSteps().map((step, index) => {
    const currentStepIndex = getSteps().findIndex(s => s.id === currentStep);
    const stepIndex = getSteps().findIndex(s => s.id === step.id);
    const isCompleted = currentStepIndex > stepIndex;
    const isActive = step.id === currentStep;
    
    return {
      id: index + 1,
      label: step.label,
      status: (isCompleted ? 'completed' : isActive ? 'current' : 'pending') as 'current' | 'completed' | 'pending' | 'disabled'
    };
  });

  return (
    <>
      {isLoading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <div className="spinner"></div>
            <div>처리 중입니다...</div>
          </LoadingSpinner>
        </LoadingOverlay>
      )}

      <InvitationLayout 
        maxWidth="800px" 
        showSteps={currentStep !== PortfolioStep.COMPLETE && currentStep !== PortfolioStep.REJECTED && currentStep !== PortfolioStep.WELCOME}
        steps={requestUserType && steps}
      >
        {currentStep === PortfolioStep.INITIAL && InitialStep()}
        {currentStep === PortfolioStep.SIGNUP && SignupStep()}
        {currentStep === PortfolioStep.COMPANY && CompanyStep()}
        {currentStep === PortfolioStep.WELCOME && <WelcomeStep notificationId={notificationId} companyCode={companyData.companyCode} navigate={navigate} />}
      </InvitationLayout>

      {/* AI 기업 정보 확인 모달 */}
      <AICompanyInfoModal
        isOpen={isAIModalOpen}
        onClose={handleAIModalClose}
        onAIConfirm={handleAIConfirm}
        onManualConfirm={handleManualConfirm}
      />

      {/* 파일 업로드 모달 */}
      <FileUploadModal
        isOpen={isFileUploadModalOpen}
        onClose={handleFileUploadModalClose}
        onFileUpload={handleFileUpload}
      />

      {/* AI 로딩 모달 */}
      <AILoadingModal
        isOpen={isAILoadingModalOpen}
        onClose={handleAILoadingModalClose}
      />
    </>
  );
};

// Styled Components

const AlertBox = styled.div<{ $variant: 'error' | 'warning' | 'info' | 'success' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  background-color: ${props => 
    props.$variant === 'error' ? semanticColors.background.danger.secondary : 
    props.$variant === 'warning' ? semanticColors.background.warning.secondary : 
    props.$variant === 'success' ? semanticColors.background.success.secondary : 
    semanticColors.background.info.secondary};
  border: 1px solid ${props => 
    props.$variant === 'error' ? semanticColors.border.dangerSecondary : 
    props.$variant === 'warning' ? semanticColors.border.warning : 
    props.$variant === 'success' ? semanticColors.border.success : 
    semanticColors.border.info};
  color: ${props => 
    props.$variant === 'error' ? semanticColors.text.onDanger.secondary : 
    props.$variant === 'warning' ? semanticColors.text.onWarning.primary : 
    props.$variant === 'success' ? semanticColors.text.onSuccess.primary : 
    semanticColors.text.onInfo.primary};
`;

const AlertText = styled.div`
  flex: 1;
  font-size: 0.875rem;
  
  small {
    display: block;
    margin-top: 0.5rem;
    opacity: 0.8;
  }
`;

const FormItem = styled.div`
  margin-bottom: 0.375rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: left;
  flex-wrap: wrap;
`;

const SuccessAnimation = styled.div`
  margin: 0 auto 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  background: ${semanticColors.background.secondary};
  border-radius: 50%;
  animation: pulse 2s infinite;
  
  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
    }
    70% {
      box-shadow: 0 0 0 15px rgba(34, 197, 94, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: ${semanticColors.text.primary};
  
  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: ${semanticColors.text.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(3px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 18px;
  color: ${semanticColors.text.tertiary};
`;

// SignupStep 전용 스타일드 컴포넌트들
const SignupFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  flex: 1;
`;

const RequestMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
`;

const FirstMessageRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 10px;
  width: 100%;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

const RequestMessage = styled.div`
${typography.label.label1}
`;

const FormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 0px;
  gap: 10px;
  width: 100%;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 8px;
  justify-content: space-between;
  width: 254px;
  flex-grow: 1;
  min-width: 0;
  
  /* width: 100% 스타일이 인라인으로 적용된 경우 */
  &[style*="width: 100%"] {
    width: 100% !important;
    flex: 1 1 100% !important;
    min-width: 0 !important;
  }
`;

const FieldLabel = styled.div<{ $isAIFilled?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px;
  gap: 2px;
  height: 21px;
  flex: none;
  order: 0;
  flex-grow: 0;

  ${props => props.$isAIFilled && `
    color: ${colorTokens.brand[800]};
  `}
`;

const LabelText = styled.span`
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #000000;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const RequiredMark = styled.span`
  width: 7px;
  height: 21px;
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: #67727A;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const ReadOnlyInput = styled.div<{ $isAIFilled?: boolean }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 12px;
  gap: 10px;
  width: 100%;
  height: 48px;
  background: #F5F5F5;
  border: 1px solid ${props => props.$isAIFilled ? colorTokens.brand[800] : '#D6D6D6'};
  border-radius: 8px;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
`;

const ReadOnlyText = styled.span<{ $isAIFilled?: boolean }>`
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  color: ${props => props.$isAIFilled ? colorTokens.brand[800] : '#000000'};
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const InfoText = styled.div`
  width: 100%;
  height: 21px;
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 150%;
  color: #000000;
  opacity: 0.5;
  flex: none;
  order: 1;
  flex-grow: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 0 0 0;
  gap: 16px;
  width: 100%;
  min-height: 40px;
  flex: none;
  align-self: stretch;
  margin-top: auto;
`;

const ErrorMessageContainer = styled.div`
  width: 100%;
  padding: 8px 0;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
`;

// CompanyStep 전용 스타일드 컴포넌트들
const CompanyFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  flex: 1;
`;

const PreparingCompanyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 32px;
  width: 100%;
  min-height: 300px;
`;

const PreparingCompanyMessage = styled.div`
  font-family: 'Pretendard';
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 150%;
  text-align: center;
  color: #000000;
`;

const PreparingCompanyButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const AIButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;

const AIIcon = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
  
  &::before {
    content: 'AI';
  }
`;

const CompanyStepHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 0 0 16px 0;
  gap: 10px;
  width: 100%;
  height: auto;
  flex: none;
  order: 0;
  flex-grow: 0;
`;

const WelcomeContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  text-align: center;
`;

const PasswordFormRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0px;
  gap: 10px;
  width: 100%;
  flex: none;
  align-self: stretch;
  flex-grow: 0;
`;

export default InvitationRegularlyReportPage; 